// src/pages/Accounts.tsx
import React, { useState, useEffect, useContext, ChangeEvent } from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  Paper,
  Alert,
  CircularProgress,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Collapse,
  Pagination,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import api from '../api/api';
import { Delete, Edit, CheckCircle, Error as ErrorIcon, Warning, AdminPanelSettings, PersonAdd, Groups, Hub, EditNote } from '@mui/icons-material';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';

// 'unchanged' — служебное значение фронта («Не менять»).
// На бэк уходят только everybody | contacts | nobody; unchanged => ключ не отправляется => None => бэк не трогает поле.
type PrivacyValue = 'unchanged' | 'everybody' | 'contacts' | 'nobody';

interface Account {
  id: string;
  name: string;
  phone_number: string;
  status: 'AUTHORIZED' | 'INACTIVE';
  meta_info?: string | null;
  busy?: {
    worker_type: string;
    job_id: string;
    locked_at: number;
  } | null;
}

interface FieldChange {
  field: string;
  status: 'success' | 'failed' | 'skipped' | 'partial';
  error?: string | null;
  old_value?: any;
  new_value?: any;
}

interface ChangeInfoResponse {
  account_id: string;
  overall_status: 'success' | 'failed' | 'skipped' | 'partial';
  changes: FieldChange[];
  errors: string[];
  warnings: string[];
}

interface AccountListResponse {
  accounts: Account[];
  total_count: number;
}

interface JobResponse {
  job_id: string;
}

interface JobStatus {
  task_id: string;
  state: 'PENDING' | 'SUCCESS' | 'FAILURE';
  result?: any;
  error?: string;
}

const Accounts: React.FC = () => {
  const auth = useContext(AuthContext);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [totalAccounts, setTotalAccounts] = useState(0);
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState<{ text: string; type: 'error' | 'success' | 'info' } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [tab, setTab] = useState<'manual' | 'tdata'>('manual');
  const [tdataFile, setTdataFile] = useState<File | null>(null);
  const [baseName, setBaseName] = useState('');
  const [metaInfo, setMetaInfo] = useState('');

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [changeResult, setChangeResult] = useState<ChangeInfoResponse | null>(null);

  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    username: '',
    bio: '',
    birthday: '',
    remove_birthday: false,
    personal_channel: '',
    remove_personal_channel: false,
    profile_photo: null as File | null,
    delete_current_photo: false,
    privacy: {
      phone_number: 'unchanged' as PrivacyValue,
      last_seen: 'unchanged' as PrivacyValue,
      profile_photo: 'unchanged' as PrivacyValue,
      forwards: 'unchanged' as PrivacyValue,
      calls: 'unchanged' as PrivacyValue,
      birthday: 'unchanged' as PrivacyValue,
    },
  });

  // Пагинация
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Модалка выдачи админ-прав
  const [grantModalOpen, setGrantModalOpen] = useState(false);
  const [grantForm, setGrantForm] = useState({
    donor_account_id: '',
    acceptor_account_ids: [] as string[],
    channel: '',
    admin_title: '',
    attach_channel_to_profile: false,
    admin_rights: {
      change_info: false,
      post_messages: true,
      edit_messages: true,
      delete_messages: true,
      ban_users: false,
      invite_users: true,
      pin_messages: true,
      add_admins: false,
      manage_call: false,
      other: false,
      manage_topics: false,
      post_stories: false,
      edit_stories: false,
      delete_stories: false,
    },
  });

  // Мониторинг задачи
  const [grantJobId, setGrantJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);

  useEffect(() => {
    if (changeResult) {
      const timer = setTimeout(() => setChangeResult(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [changeResult]);

  // Поллинг статуса задачи
  useEffect(() => {
    if (!grantJobId || !grantModalOpen) return;

    const interval = setInterval(async () => {
      try {
        const res = await api.get<JobStatus>(`parse/job_status/${grantJobId}`);
        setJobStatus(res.data);

        // Останавливаем поллинг при завершении
        if (res.data.state === 'SUCCESS' || res.data.state === 'FAILURE') {
          clearInterval(interval);
        }
      } catch (err) {
        console.error('Ошибка проверки статуса:', err);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [grantJobId, grantModalOpen]);

  const fetchAccounts = async () => {
    if (!auth?.isAuthenticated) {
      setMessage({ text: 'Авторизуйтесь для просмотра аккаунтов', type: 'error' });
      return;
    }
    setIsLoading(true);
    try {
      const response = await api.get<AccountListResponse>('/accounts', {
        params: { limit: pageSize, offset: (page - 1) * pageSize },
      });
      setAccounts(response.data.accounts || []);
      setTotalAccounts(response.data.total_count || 0);
      setMessage(null);
    } catch (error: any) {
      setMessage({ text: `Ошибка загрузки: ${error.response?.data?.detail || error.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, [auth?.isAuthenticated, page]);

  const startActivation = async (id: string) => {
    setIsLoading(true);
    setMessage(null);
    try {
      await api.get(`/accounts/auth/${id}`);
      setActivatingId(id);
      setMessage({
        text: 'Введите код, который прислал вам Telegram, и пароль двухфакторной аутентификации, если он установлен.',
        type: 'info',
      });
    } catch (error: any) {
      setMessage({
        text: `Ошибка запуска активации: ${error.response?.data?.detail || error.message}`,
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const confirmActivation = async () => {
    if (!activatingId) return;
    setIsLoading(true);
    try {
      await api.post(`/accounts/auth/confirm_code/${activatingId}`, { code, password });
      setMessage({ text: 'Аккаунт активирован!', type: 'success' });
      setActivatingId(null);
      setCode('');
      setPassword('');
      fetchAccounts();
    } catch (error: any) {
      setMessage({ text: `Ошибка подтверждения: ${error.response?.data?.detail || error.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteAccount = async (id: string) => {
    if (isLoading || !window.confirm('Удалить аккаунт навсегда?')) return;
    setIsLoading(true);
    try {
      await api.delete(`/accounts/${id}`);
      setAccounts(accounts.filter((acc) => acc.id !== id));
      setMessage({ text: 'Аккаунт удалён!', type: 'success' });
    } catch (error: any) {
      setMessage({ text: `Ошибка удаления: ${error.response?.data?.detail || error.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const openEditModal = (account: Account) => {
    setEditingAccount(account);
    setEditForm({
      first_name: '',
      last_name: '',
      username: '',
      bio: '',
      birthday: '',
      remove_birthday: false,
      personal_channel: '',
      remove_personal_channel: false,
      profile_photo: null,
      delete_current_photo: false,
      privacy: {
        phone_number: 'unchanged',
        last_seen: 'unchanged',
        profile_photo: 'unchanged',
        forwards: 'unchanged',
        calls: 'unchanged',
        birthday: 'unchanged',
      },
    });
    setEditModalOpen(true);
  };

  const handleEditProfile = async () => {
    if (!editingAccount) return;

    setIsLoading(true);
    const formData = new FormData();
    formData.append('account_id', editingAccount.id);

    if (editForm.first_name) formData.append('first_name', editForm.first_name);
    if (editForm.last_name) formData.append('last_name', editForm.last_name);
    if (editForm.username) formData.append('username', editForm.username);
    if (editForm.bio) formData.append('bio', editForm.bio);
    if (editForm.birthday) formData.append('birthday', editForm.birthday);
    if (editForm.remove_birthday) formData.append('remove_birthday', 'true');
    if (editForm.personal_channel) formData.append('personal_channel', editForm.personal_channel);
    if (editForm.remove_personal_channel) formData.append('remove_personal_channel', 'true');
    if (editForm.profile_photo) formData.append('profile_photo', editForm.profile_photo);
    if (editForm.delete_current_photo) formData.append('delete_current_photo', 'true');

    // Приватность: unchanged («Не менять») пропускаем — бэк получит None и не тронет поле.
    // everybody/contacts/nobody отправляем явно.
    Object.entries(editForm.privacy).forEach(([key, value]) => {
      if (value !== 'unchanged') {
        formData.append(`privacy_settings[${key}]`, value);
      }
    });

    try {
      const res = await api.post('/accounts/change_info', formData);
      setChangeResult(res.data);
      setEditModalOpen(false);
      fetchAccounts();
    } catch (error: any) {
      setMessage({
        text: `Ошибка обновления профиля: ${error.response?.data?.detail || error.message}`,
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTdataImport = async () => {
    if (!tdataFile || !baseName.trim()) {
      setMessage({ text: 'Выберите ZIP-файл и укажите базовое имя', type: 'error' });
      return;
    }

    setIsLoading(true);
    const formData = new FormData();
    formData.append('tdata_file', tdataFile);
    formData.append('base_name', baseName.trim());
    if (metaInfo.trim()) formData.append('meta_info', metaInfo.trim());

    try {
      const response = await api.post('accounts/import/tdata', formData);
      const result = response.data;
      setMessage({
        text: `Импорт завершён: ${result.successfully_imported} успешно, ${result.failed} ошибок`,
        type: 'success',
      });
      fetchAccounts();
      setTdataFile(null);
      setBaseName('');
      setMetaInfo('');
    } catch (error: any) {
      setMessage({ text: `Ошибка импорта: ${error.response?.data?.detail || error.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setTdataFile(e.target.files[0]);
    }
  };

  const openGrantModal = () => {
    setGrantForm({
      donor_account_id: '',
      acceptor_account_ids: [],
      channel: '',
      admin_title: '',
      attach_channel_to_profile: true,
      admin_rights: {
        change_info: false,
        post_messages: true,
        edit_messages: true,
        delete_messages: true,
        ban_users: false,
        invite_users: true,
        pin_messages: true,
        add_admins: false,
        manage_call: false,
        other: false,
        manage_topics: false,
        post_stories: false,
        edit_stories: false,
        delete_stories: false,
      },
    });
    setGrantJobId(null);
    setJobStatus(null);
    setGrantModalOpen(true);
  };

  const handleGrantRights = async () => {
    if (!grantForm.donor_account_id || grantForm.acceptor_account_ids.length === 0 || !grantForm.channel.trim()) {
      setMessage({ text: 'Выберите донора, хотя бы одного акцептора и укажите канал', type: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post<JobResponse>('accounts/grant_rights', grantForm);
      setGrantJobId(response.data.job_id);
    } catch (error: any) {
      setMessage({
        text: `Ошибка запуска задачи: ${error.response?.data?.detail || error.message}`,
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getTabStyle = (current: 'manual' | 'tdata') => ({
    flex: 1,
    py: 2,
    borderRadius: 0,
    bgcolor: tab === current ? '#E0F2FE' : 'transparent',
    color: tab === current ? '#1E40AF' : '#6B7280',
    fontWeight: tab === current ? 600 : 400,
    '&:hover': { bgcolor: tab === current ? '#DBEAFE' : '#F3F4F6' },
  });

  const totalPages = Math.ceil(totalAccounts / pageSize);

  // Единый стиль кнопок в шапке «Мои аккаунты»
  const headerBtnSx = {
    bgcolor: '#1E3A8A',
    '&:hover': { bgcolor: '#1E40AF' },
    textTransform: 'none' as const,
    whiteSpace: 'nowrap' as const,
    fontSize: '0.9rem',
    px: 2.5,
    py: 1,
  };

  if (isLoading && accounts.length === 0) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography>Загрузка...</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      {/* Верхний блок инструкций */}
      <Box sx={{ bgcolor: '#F9FAFB', p: 4, borderRadius: 2, mb: 6 }}>
        <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 3, fontWeight: 600 }}>
          Как добавить и активировать аккаунт Telegram для работы с Typhon?
        </Typography>
        <Typography sx={{ color: '#4B5563', mb: 3, lineHeight: 1.7 }}>
          Аккаунты — это ваши Telegram-профили, через которые работают все функции нашего сервиса. Мы не
          рекомендуем использовать свои личные аккаунты для работы с Typhon.
        </Typography>

        <Typography variant="subtitle1" sx={{ color: '#1E40AF', fontWeight: 600, mb: 2 }}>
          Активация аккаунта вручную
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 4 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Шаг 1: Получите API ID и Hash</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem', mb: 1 }}>
              Зайдите на <strong><a href="https://my.telegram.org" target="_blank" rel="noopener noreferrer" style={{ color: '#1E40AF' }}>my.telegram.org</a></strong>
            </Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem', mb: 1 }}>
              • Войдите по номеру телефона<br />
              • Перейдите в <strong>API development tools</strong><br />
              • Создайте приложение (название любое)<br />
              • Скопируйте <strong>api_id</strong> и <strong>api_hash</strong>
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Шаг 2: Добавьте аккаунт</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem', mb: 1 }}>
              Нажмите кнопку <strong>«Добавить аккаунт»</strong>
            </Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem', mb: 1 }}>
              • Введите номер телефона (с кодом страны, например: <code>+79991234567</code>)<br />
              • Вставьте <strong>api_id</strong> и <strong>api_hash</strong><br />
              • Придумайте имя (например: <em>Основной</em>, <em>Реклама</em>)
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Шаг 3: Активируйте</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem', mb: 1 }}>
              Нажмите <strong>«Активировать»</strong> напротив аккаунта
            </Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem', mb: 1 }}>
              • Telegram пришлёт код в приложение<br />
              • Введите код<br />
              • Если включена 2FA — введите пароль<br />
              • Готово! Аккаунт активен
            </Typography>
          </Box>
        </Box>

        <Typography variant="subtitle1" sx={{ color: '#1E40AF', fontWeight: 600, mb: 2 }}>
          Импорт аккаунтов при помощи tdata
        </Typography>
        <Typography sx={{ color: '#4B5563', mb: 3, lineHeight: 1.7 }}>
          Загрузите ZIP-архив с папками tdata. Архив не должен превышать 200 МБ. Структура: в архиве должны
          быть папки tdata для каждого аккаунта (на любом уровне вложенности).
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Пример структуры архива</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              • tdata_acc1/tdata/ (содержит key_data, map и другие файлы)<br />
              • tdata_acc2/tdata/<br />
              • nested/tdata_acc3/tdata/<br />
              (и т.д. — папки tdata могут быть вложенными)
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Шаги импорта</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              • Выберите ZIP-файл<br />
              • Укажите базовое имя (префикс для импортированных аккаунтов)<br />
              • Добавьте общую мета-информацию (опционально)<br />
              • Нажмите "Импортировать"
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#EF4444', fontWeight: 500, mb: 1 }}>Важно</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              • Архив ≤ 200 МБ<br />
              • Каждый tdata — отдельный аккаунт<br />
              • После импорта активируйте аккаунты<br />
              • Используйте только свои аккаунты
            </Typography>
          </Box>
        </Box>

        <Typography sx={{ color: '#1E40AF', mt: 4, fontWeight: 600, fontStyle: 'italic' }}>
          После добавления и активации — вы можете использовать аккаунт в любой функции сервиса.
        </Typography>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {changeResult && (
        <Alert
            severity={
              changeResult.overall_status === 'success' ? 'success' :
                  changeResult.overall_status === 'partial' ? 'warning' :
                      changeResult.overall_status === 'skipped' ? 'info' :   // ← добавить
                          'error'
            }
            icon={
              changeResult.overall_status === 'success' ? <CheckCircle/> :
                  changeResult.overall_status === 'partial' ? <Warning/> :
                      changeResult.overall_status === 'skipped' ? undefined :  // ← дефолтная info иконка
                          <ErrorIcon/>
            }
          sx={{ mb: 3 }}
          onClose={() => setChangeResult(null)}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            {changeResult.overall_status === 'success' ? 'Профиль успешно обновлён!' :
                changeResult.overall_status === 'partial' ? 'Профиль частично обновлён' :
                    changeResult.overall_status === 'skipped' ? 'Ничего не изменилось — настройки уже актуальны' :  // ← добавить
                        'Не удалось обновить профиль'}
          </Typography>
          <Collapse in={true}>
            <Box sx={{ mt: 2 }}>
              {changeResult.changes.map((change, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  {change.status === 'success' ? <CheckCircle fontSize="small" color="success" /> :
                    change.status === 'failed' ? <ErrorIcon fontSize="small" color="error" /> :
                      <Warning fontSize="small" color="warning" />}
                  <Typography variant="body2">
                    <strong>{change.field}:</strong>{' '}
                    {change.status === 'success' ? 'изменено успешно' :
                      change.status === 'failed' ? `ошибка: ${change.error || 'неизвестно'}` :
                        'пропущено'}
                  </Typography>
                </Box>
              ))}
              {changeResult.warnings.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="warning.main">Предупреждения:</Typography>
                  {changeResult.warnings.map((w, i) => (
                    <Typography key={i} variant="body2" sx={{ ml: 2 }}>{w}</Typography>
                  ))}
                </Box>
              )}
              {changeResult.errors.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="error.main">Ошибки:</Typography>
                  {changeResult.errors.map((e, i) => (
                    <Typography key={i} variant="body2" sx={{ ml: 2 }}>{e}</Typography>
                  ))}
                </Box>
              )}
            </Box>
          </Collapse>
        </Alert>
      )}



      {/* Список аккаунтов */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{
          p: 3,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}>
          <Typography variant="h5" sx={{ color: '#1E3A8A', fontWeight: 600 }}>
            Мои аккаунты
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              component={Link}
              to="/accounts/add"
              disabled={isLoading}
              startIcon={<PersonAdd />}
              sx={headerBtnSx}
            >
              Добавить
            </Button>
            <Button
              variant="contained"
              component={Link}
              to="/groups"
              startIcon={<Groups />}
              sx={headerBtnSx}
              disabled={isLoading || accounts.length === 0}
            >
              Группы
            </Button>
            <Button
              variant="contained"
              onClick={openGrantModal}
              startIcon={<AdminPanelSettings />}
              sx={headerBtnSx}
              disabled={isLoading || accounts.length === 0}
            >
              Админ-права
            </Button>
            <Button
              variant="contained"
              component={Link}
              to="/proxy-channels"
              startIcon={<Hub />}
              sx={headerBtnSx}
              disabled={isLoading || accounts.length === 0}
            >
              Прокси-каналы
            </Button>
            <Button
              variant="contained"
              component={Link}
              to="/bulk-change-info"
              startIcon={<EditNote />}
              sx={headerBtnSx}
              disabled={isLoading || accounts.length === 0}
            >
              Массовое изменение
            </Button>
          </Box>
        </Box>

        <Box sx={{ p: 3 }}>
          {accounts.length > 0 ? (
            <>
              <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }}>Имя</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }}>Телефон</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }}>Статус</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }}>Занятость</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }} align="right">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accounts.map(acc => (
                    <TableRow key={acc.id} sx={{ '&:hover': { bgcolor: '#F9FAFB' } }}>
                      <TableCell>
                        <Typography
                          component={Link}
                          to={`/accounts/${acc.id}`}
                          sx={{
                            color: '#1E40AF',
                            textDecoration: 'none',
                            fontWeight: 500,
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {acc.name}
                        </Typography>
                      </TableCell>
                      <TableCell>{acc.phone_number}</TableCell>
                      <TableCell>
                        <Box sx={{
                          color: acc.status === 'AUTHORIZED' ? '#059669' : '#DC2626',
                          fontWeight: 500,
                          fontSize: '0.875rem'
                        }}>
                          {acc.status === 'AUTHORIZED' ? 'Активен' : 'Неактивен'}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {acc.busy ? (
                          <Chip
                            label={acc.busy.worker_type}
                            size="small"
                            color="warning"
                            variant="filled"
                            sx={{
                              bgcolor: '#F59E0B',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.8rem',
                              height: 28,
                            }}
                          />
                        ) : (
                          <Typography sx={{ color: '#9CA3AF', fontSize: '0.85rem' }}>Свободен</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1}}>
                          {acc.status !== 'AUTHORIZED' && (
                              <Button
                                  onClick={() => startActivation(acc.id)}
                                  size="small"
                                  disabled={isLoading}
                                  sx={{
                                    bgcolor: '#10B981',
                                    color: 'white',
                                    '&:hover': {bgcolor: '#059669'},
                                    minWidth: 'auto',
                                    px: 1.5
                                  }}
                              >
                                Активировать
                              </Button>
                          )}
                          <IconButton
                              size="small"
                              onClick={() => openEditModal(acc)}
                              disabled={isLoading || acc.status !== 'AUTHORIZED'}
                              sx={{color: '#1E40AF', '&:hover': {bgcolor: '#DBEAFE'}}}
                              title="Редактировать профиль в Telegram"
                          >
                            <Edit fontSize="small"/>
                          </IconButton>
                          <IconButton
                              size="small"
                              onClick={() => deleteAccount(acc.id)}
                              disabled={isLoading}
                              sx={{color: '#EF4444', '&:hover': {bgcolor: '#FECACA'}}}
                          >
                            <Delete fontSize="small"/>
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
                </TableContainer>

              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, newPage) => setPage(newPage)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          ) : (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              У вас пока нет аккаунтов. Добавьте первый!
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Форма активации */}
      {activatingId && (
        <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
          <Typography sx={{ color: '#1E3A8A', fontWeight: 600, mb: 2 }}>
            Активация аккаунта
          </Typography>
          <Typography sx={{ color: '#4B5563', mb: 3, fontSize: '0.95rem' }}>
            {message?.text}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                label="Код из Telegram"
                value={code}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setCode(e.target.value)}
                fullWidth
                disabled={isLoading}
                placeholder="123456"
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="Пароль 2FA (если включён)"
                value={password}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                fullWidth
                disabled={isLoading}
                type="password"
              />
            </Grid>
            <Grid size={12}>
              <Button
                onClick={confirmActivation}
                variant="contained"
                disabled={isLoading || !code.trim()}
                fullWidth
                sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' }, py: 1.5 }}
              >
                {isLoading ? <CircularProgress size={20} /> : 'Подтвердить'}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Модальное окно редактирования профиля */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: '#1E3A8A', fontWeight: 600 }}>
          Редактировать профиль: {editingAccount?.name} ({editingAccount?.phone_number})
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={6}>
              <TextField
                label="Имя"
                value={editForm.first_name}
                onChange={e => setEditForm(prev => ({ ...prev, first_name: e.target.value }))}
                fullWidth
                helperText="Оставьте пустым, если не меняете"
              />
            </Grid>
            <Grid size={6}>
              <TextField
                label="Фамилия"
                value={editForm.last_name}
                onChange={e => setEditForm(prev => ({ ...prev, last_name: e.target.value }))}
                fullWidth
                helperText="Оставьте пустым, если не меняете"
              />
            </Grid>

            <Grid size={12}>
              <TextField
                label="Username"
                value={editForm.username}
                onChange={e => setEditForm(prev => ({ ...prev, username: e.target.value }))}
                fullWidth
                placeholder="Без @"
                helperText="Латиница, цифры и _, 5–32 символа"
              />
            </Grid>

            <Grid size={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={600} color="#1E40AF">
                  Описание (Bio)
                </Typography>
                <Button variant="outlined" size="small" startIcon={<EmojiEmotionsIcon />}
                        onClick={() => setShowEmoji(p => !p)}>
                  Эмодзи
                </Button>
              </Box>
              {showEmoji && (
                <Box sx={{ mb: 2 }}>
                  <Picker data={data} onEmojiSelect={(emoji: any) => {
                    setEditForm(prev => ({ ...prev, bio: prev.bio + emoji.native }));
                    setShowEmoji(false);
                  }} theme="light" />
                </Box>
              )}
              <TextField
                value={editForm.bio}
                onChange={e => setEditForm(prev => ({ ...prev, bio: e.target.value }))}
                fullWidth
                multiline
                rows={4}
                placeholder="До 70 символов"
                helperText="Оставьте пустым, если не меняете"
              />
            </Grid>

            <Grid size={6}>
              <TextField
                label="Дата рождения"
                type="date"
                value={editForm.birthday}
                onChange={e => setEditForm(prev => ({ ...prev, birthday: e.target.value }))}
                fullWidth
                disabled={editForm.remove_birthday}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={6} sx={{ display: 'flex', alignItems: 'end' }}>
              <FormControlLabel
                control={<Checkbox checked={editForm.remove_birthday}
                                   onChange={e => setEditForm(prev => ({
                                     ...prev,
                                     remove_birthday: e.target.checked
                                   }))} />}
                label="Удалить дату рождения"
              />
            </Grid>

            <Grid size={12}>
              <TextField
                label="Прикреплённый канал"
                value={editForm.personal_channel}
                onChange={e => setEditForm(prev => ({ ...prev, personal_channel: e.target.value }))}
                fullWidth
                placeholder="@mychannel или -100..."
                helperText="Оставьте пустым, если не меняете"
              />
            </Grid>
            <Grid size={12}>
              <FormControlLabel
                control={<Checkbox checked={editForm.remove_personal_channel}
                                   onChange={e => setEditForm(prev => ({
                                     ...prev,
                                     remove_personal_channel: e.target.checked
                                   }))} />}
                label="Открепить текущий канал"
              />
            </Grid>

            <Grid size={12}>
              <Typography variant="subtitle2" gutterBottom>Фото профиля</Typography>
              <Button variant="outlined" component="label" startIcon={<AttachFileIcon />} fullWidth sx={{ mb: 2 }}>
                Загрузить новое фото
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={e => e.target.files?.[0] && setEditForm(prev => ({
                    ...prev,
                    profile_photo: e.target.files![0]
                  }))}
                />
              </Button>
              {editForm.profile_photo && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">{editForm.profile_photo.name}</Typography>
                  <IconButton size="small" onClick={() => setEditForm(prev => ({ ...prev, profile_photo: null }))}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              <FormControlLabel
                control={<Checkbox checked={editForm.delete_current_photo}
                                   onChange={e => setEditForm(prev => ({
                                     ...prev,
                                     delete_current_photo: e.target.checked
                                   }))} />}
                label="Удалить текущее фото профиля"
              />
            </Grid>

            <Grid size={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#1E40AF', fontWeight: 600, mb: 3 }}>
                Кто может видеть мои данные
              </Typography>
            </Grid>

            <Grid size={12}>
              <TextField
                select
                fullWidth
                label="Номер телефона"
                value={editForm.privacy.phone_number}
                onChange={e =>
                  setEditForm(prev => ({
                    ...prev,
                    privacy: { ...prev.privacy, phone_number: e.target.value as any }
                  }))
                }
                sx={{ mb: 2 }}
              >
                <MenuItem value="unchanged">Не менять</MenuItem>
                <MenuItem value="everybody">Все</MenuItem>
                <MenuItem value="contacts">Мои контакты</MenuItem>
                <MenuItem value="nobody">Никто</MenuItem>
              </TextField>
            </Grid>

            <Grid size={12}>
              <TextField
                select
                fullWidth
                label="Последний визит и онлайн"
                value={editForm.privacy.last_seen}
                onChange={e =>
                  setEditForm(prev => ({
                    ...prev,
                    privacy: { ...prev.privacy, last_seen: e.target.value as any }
                  }))
                }
                sx={{ mb: 2 }}
              >
                <MenuItem value="unchanged">Не менять</MenuItem>
                <MenuItem value="everybody">Все</MenuItem>
                <MenuItem value="contacts">Мои контакты</MenuItem>
                <MenuItem value="nobody">Никто</MenuItem>
              </TextField>
            </Grid>

            <Grid size={12}>
              <TextField
                select
                fullWidth
                label="Фото профиля"
                value={editForm.privacy.profile_photo}
                onChange={e =>
                  setEditForm(prev => ({
                    ...prev,
                    privacy: { ...prev.privacy, profile_photo: e.target.value as any }
                  }))
                }
                sx={{ mb: 2 }}
              >
                <MenuItem value="unchanged">Не менять</MenuItem>
                <MenuItem value="everybody">Все</MenuItem>
                <MenuItem value="contacts">Мои контакты</MenuItem>
                <MenuItem value="nobody">Никто</MenuItem>
              </TextField>
            </Grid>

            <Grid size={12}>
              <TextField
                select
                fullWidth
                label="Пересылаемые сообщения"
                value={editForm.privacy.forwards}
                onChange={e =>
                  setEditForm(prev => ({
                    ...prev,
                    privacy: { ...prev.privacy, forwards: e.target.value as any }
                  }))
                }
                sx={{ mb: 2 }}
              >
                <MenuItem value="unchanged">Не менять</MenuItem>
                <MenuItem value="everybody">Все</MenuItem>
                <MenuItem value="contacts">Мои контакты</MenuItem>
                <MenuItem value="nobody">Никто</MenuItem>
              </TextField>
            </Grid>

            <Grid size={12}>
              <TextField
                select
                fullWidth
                label="Звонки"
                value={editForm.privacy.calls}
                onChange={e =>
                  setEditForm(prev => ({
                    ...prev,
                    privacy: { ...prev.privacy, calls: e.target.value as any }
                  }))
                }
                sx={{ mb: 2 }}
              >
                <MenuItem value="unchanged">Не менять</MenuItem>
                <MenuItem value="everybody">Все</MenuItem>
                <MenuItem value="contacts">Мои контакты</MenuItem>
                <MenuItem value="nobody">Никто</MenuItem>
              </TextField>
            </Grid>

            <Grid size={12}>
              <TextField
                select
                fullWidth
                label="Дату рождения"
                value={editForm.privacy.birthday}
                onChange={e =>
                  setEditForm(prev => ({
                    ...prev,
                    privacy: { ...prev.privacy, birthday: e.target.value as any }
                  }))
                }
                sx={{ mb: 2 }}
              >
                <MenuItem value="unchanged">Не менять</MenuItem>
                <MenuItem value="everybody">Все</MenuItem>
                <MenuItem value="contacts">Мои контакты</MenuItem>
                <MenuItem value="nobody">Никто</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)}>Отмена</Button>
          <Button
            onClick={handleEditProfile}
            variant="contained"
            disabled={isLoading}
            sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' } }}
          >
            {isLoading ? <CircularProgress size={20} /> : 'Сохранить изменения'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* МОДАЛКА ВЫДАЧИ АДМИН-ПРАВ С МОНИТОРИНГОМ */}
      <Dialog open={grantModalOpen} onClose={() => setGrantModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: '#1E3A8A', fontWeight: 600 }}>
          Выдать права администратора
        </DialogTitle>
        <DialogContent dividers>
          {grantJobId ? (
            <Box sx={{ py: 4 }}>
              <Typography variant="h6" gutterBottom color="primary">
                Задача запущена (ID: {grantJobId})
              </Typography>

              {jobStatus ? (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Статус: <strong>{jobStatus.state}</strong>
                  </Typography>

                  {jobStatus.state === 'PENDING' && (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <CircularProgress size={40} />
                      <Typography sx={{ mt: 2 }}>Выполняется... (проверка каждые 3 секунды)</Typography>
                    </Box>
                  )}

                  {jobStatus.state === 'SUCCESS' && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      Успешно завершено!
                      {jobStatus.result && (
                        <Box sx={{ mt: 2, bgcolor: '#F3F4F6', p: 2, borderRadius: 1, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(jobStatus.result, null, 2)}
                        </Box>
                      )}
                    </Alert>
                  )}

                  {jobStatus.state === 'FAILURE' && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      Ошибка: {jobStatus.error || 'Неизвестная ошибка'}
                    </Alert>
                  )}
                </Box>
              ) : (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <CircularProgress size={30} />
                  <Typography sx={{ mt: 1 }}>Загрузка статуса...</Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Grid container spacing={3}>
              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel id="donor-label">Донор (действующий админ)</InputLabel>
                  <Select
                    labelId="donor-label"
                    label="Донор (действующий админ)"
                    value={grantForm.donor_account_id}
                    onChange={(e) => setGrantForm(prev => ({ ...prev, donor_account_id: e.target.value as string }))}
                  >
                    {accounts
                      .filter(acc => acc.status === 'AUTHORIZED')
                      .map(acc => (
                        <MenuItem key={acc.id} value={acc.id}>
                          {acc.name} ({acc.phone_number})
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel id="acceptors-label">Акцепторы (кому выдавать права)</InputLabel>
                  <Select
                    labelId="acceptors-label"
                    label="Акцепторы (кому выдавать права)"
                    multiple
                    value={grantForm.acceptor_account_ids}
                    onChange={(e) => setGrantForm(prev => ({ ...prev, acceptor_account_ids: e.target.value as string[] }))}
                    renderValue={(selected: string[]) => `${selected.length} аккаунтов выбрано`}
                  >
                    {accounts
                      .filter(acc => acc.id !== grantForm.donor_account_id && acc.status === 'AUTHORIZED')
                      .map(acc => (
                        <MenuItem key={acc.id} value={acc.id}>
                          <Checkbox checked={grantForm.acceptor_account_ids.includes(acc.id)} />
                          {acc.name} ({acc.phone_number})
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={12}>
                <TextField
                  label="Канал / группа"
                  value={grantForm.channel}
                  onChange={(e) => setGrantForm(prev => ({ ...prev, channel: e.target.value }))}
                  fullWidth
                  placeholder="@channel, https://t.me/+xxx или -1001234567890"
                  helperText="Username, пригласительная ссылка или числовой ID"
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  label="Титул администратора (опционально)"
                  value={grantForm.admin_title}
                  onChange={(e) => setGrantForm(prev => ({ ...prev, admin_title: e.target.value }))}
                  fullWidth
                  placeholder="Главный модератор"
                  helperText="До 16 символов. Отображается в списке админов"
                />
              </Grid>

              <Grid size={12}>
                <Typography variant="subtitle1" gutterBottom sx={{ color: '#1E40AF', mt: 2 }}>
                  Права администратора
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <FormControlLabel
                      control={<Checkbox checked={grantForm.admin_rights.change_info} onChange={e => setGrantForm(prev => ({ ...prev, admin_rights: { ...prev.admin_rights, change_info: e.target.checked } }))} />}
                      label="Изменять информацию канала"
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormControlLabel
                      control={<Checkbox checked={grantForm.admin_rights.post_messages} onChange={e => setGrantForm(prev => ({ ...prev, admin_rights: { ...prev.admin_rights, post_messages: e.target.checked } }))} />}
                      label="Отправлять сообщения"
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormControlLabel
                      control={<Checkbox checked={grantForm.admin_rights.edit_messages} onChange={e => setGrantForm(prev => ({ ...prev, admin_rights: { ...prev.admin_rights, edit_messages: e.target.checked } }))} />}
                      label="Редактировать сообщения"
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormControlLabel
                      control={<Checkbox checked={grantForm.admin_rights.delete_messages} onChange={e => setGrantForm(prev => ({ ...prev, admin_rights: { ...prev.admin_rights, delete_messages: e.target.checked } }))} />}
                      label="Удалять сообщения"
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormControlLabel
                      control={<Checkbox checked={grantForm.admin_rights.ban_users} onChange={e => setGrantForm(prev => ({ ...prev, admin_rights: { ...prev.admin_rights, ban_users: e.target.checked } }))} />}
                      label="Банить пользователей"
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormControlLabel
                      control={<Checkbox checked={grantForm.admin_rights.invite_users} onChange={e => setGrantForm(prev => ({ ...prev, admin_rights: { ...prev.admin_rights, invite_users: e.target.checked } }))} />}
                      label="Приглашать пользователей"
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormControlLabel
                      control={<Checkbox checked={grantForm.admin_rights.pin_messages} onChange={e => setGrantForm(prev => ({ ...prev, admin_rights: { ...prev.admin_rights, pin_messages: e.target.checked } }))} />}
                      label="Закреплять сообщения"
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormControlLabel
                      control={<Checkbox checked={grantForm.admin_rights.add_admins} onChange={e => setGrantForm(prev => ({ ...prev, admin_rights: { ...prev.admin_rights, add_admins: e.target.checked } }))} />}
                      label="Добавлять новых администраторов"
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormControlLabel
                      control={<Checkbox checked={grantForm.admin_rights.manage_call} onChange={e => setGrantForm(prev => ({ ...prev, admin_rights: { ...prev.admin_rights, manage_call: e.target.checked } }))} />}
                      label="Управлять звонками"
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormControlLabel
                      control={<Checkbox checked={grantForm.admin_rights.manage_topics} onChange={e => setGrantForm(prev => ({ ...prev, admin_rights: { ...prev.admin_rights, manage_topics: e.target.checked } }))} />}
                      label="Управлять темами"
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormControlLabel
                      control={<Checkbox checked={grantForm.admin_rights.post_stories} onChange={e => setGrantForm(prev => ({ ...prev, admin_rights: { ...prev.admin_rights, post_stories: e.target.checked } }))} />}
                      label="Публиковать истории"
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormControlLabel
                      control={<Checkbox checked={grantForm.admin_rights.edit_stories} onChange={e => setGrantForm(prev => ({ ...prev, admin_rights: { ...prev.admin_rights, edit_stories: e.target.checked } }))} />}
                      label="Редактировать истории"
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormControlLabel
                      control={<Checkbox checked={grantForm.admin_rights.delete_stories} onChange={e => setGrantForm(prev => ({ ...prev, admin_rights: { ...prev.admin_rights, delete_stories: e.target.checked } }))} />}
                      label="Удалять истории"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGrantModalOpen(false)}>Закрыть</Button>
          {!grantJobId && (
            <Button
              onClick={handleGrantRights}
              variant="contained"
              disabled={
                isLoading ||
                !grantForm.donor_account_id ||
                grantForm.acceptor_account_ids.length === 0 ||
                !grantForm.channel.trim()
              }
              sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#7C3AED' } }}
            >
              {isLoading ? <CircularProgress size={20} /> : 'Запустить задачу'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Accounts;